import React, { useState } from "react"
import { useMutation } from "@apollo/client"
import { CustomState, CustomStateType, OrderCustomState } from "lib/types/generated/graphql-types"
import {
  ChevronDown,
  ChevronUp,
  Container,
  CustomStateIcon,
  IconWrapper,
  Line,
  NoResults,
  SearchWrapper,
  StateList,
  StateRow,
  StateRowName,
  StateRowOperationTag,
  StateRowOperations,
  Trigger,
  TriggerLabel
} from "./AddCustomStateToOrder.styled"
import { TypeBadge } from "../OrderOverview/OrderCustomStates.styled"
import ADD_CUSTOM_STATE_TO_ORDER from "../../../graphql/mutations/settings/customState/AddCustomStateToOrder"
import { handleErrorMessages } from "helpers/errors"
import PrimaryButton from "../../Ui/Buttons/PrimaryButton"
import { formatOperation } from "../helpers"
import { ReactComponent as BlockIcon } from "images/icons/circle-xmark.svg"
import Search from "../../Ui/Search"

type Props = {
  orderId: string
  customStates: CustomState[]
  addedCustomStates: OrderCustomState[]
  addCustomStateToHistory: (customStateId: string) => void
  refetch: () => void
}

const AddCustomStateToOrder = ({
  orderId,
  customStates,
  addedCustomStates,
  addCustomStateToHistory,
  refetch
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [loadingStateId, setLoadingStateId] = useState<string | null>(null)

  const stateAdded = (id: string) =>
    !!addedCustomStates.find((customState) => customState.id === id)

  const availableStates = customStates.filter((state) => !stateAdded(state.id))

  const filteredStates = availableStates.filter((state) => {
    if (!searchTerm) return true
    const search = searchTerm.toLowerCase()
    const description = (state.description ?? state.id).toLowerCase()
    return description.includes(search) || state.id.toLowerCase().includes(search)
  })

  const [addCustomState, { loading }] = useMutation(ADD_CUSTOM_STATE_TO_ORDER, {
    onCompleted: () => {
      if (loadingStateId) addCustomStateToHistory(loadingStateId)
      setLoadingStateId(null)
      refetch()
    },
    onError: (err) => {
      setLoadingStateId(null)
      handleErrorMessages(err)
    }
  })

  const addCustomStateToOrder = (stateId: string) => {
    setLoadingStateId(stateId)
    addCustomState({ variables: { stateId, orderId } })
  }

  return (
    <>
      <Line />
      <Container>
        <Trigger onClick={() => setIsOpen(!isOpen)}>
          <IconWrapper>
            <CustomStateIcon />
          </IconWrapper>
          <TriggerLabel>Select a custom state</TriggerLabel>
          {isOpen ? <ChevronUp /> : <ChevronDown />}
        </Trigger>
        {isOpen && (
          <>
            <SearchWrapper>
              <Search
                handleOnChange={setSearchTerm}
                placeholder="Search custom states..."
                defaultValue={searchTerm}
                padding="0"
              />
            </SearchWrapper>
            <StateList>
              {filteredStates.length === 0 ? (
                <NoResults>No matching custom states</NoResults>
              ) : (
                filteredStates.map((state) => {
                  const isBlocking = state.type === CustomStateType.Blocking
                  return (
                    <StateRow key={state.id}>
                      <TypeBadge $blockingState={isBlocking}>
                        {isBlocking ? (
                          <>
                            <BlockIcon /> Blocking
                          </>
                        ) : (
                          "Informative"
                        )}
                      </TypeBadge>
                      <StateRowName>{state.description ?? state.id}</StateRowName>
                      {isBlocking &&
                        state.operations &&
                        state.operations.length > 0 && (
                          <StateRowOperations>
                            {state.operations.map((operation) => (
                              <StateRowOperationTag key={operation}>
                                <BlockIcon />
                                {formatOperation(operation)}
                              </StateRowOperationTag>
                            ))}
                          </StateRowOperations>
                        )}
                      <PrimaryButton
                        small
                        handleClick={() => addCustomStateToOrder(state.id)}
                        loading={loadingStateId === state.id}
                        disabled={loading}
                      >
                        Add
                      </PrimaryButton>
                    </StateRow>
                  )
                })
              )}
            </StateList>
          </>
        )}
      </Container>
      <Line />
    </>
  )
}

export default AddCustomStateToOrder
